/**
 * HotkeyHelpFloatingButton — floating "?" button в правом нижнем углу,
 * открывает существующий `<HotkeyHelpModal />` (`?` hotkey уже работает).
 *
 * Зачем: HotkeyHelpModal был discoverable только через хоткей `?` или `Shift+/`.
 * Power-users знают, новички — нет. Floating "?" — стандартный UX-паттерн для
 * "help / shortcuts" (Linear, Slack, Notion).
 *
 * Тонкости:
 * - Skip rendering на mobile (нет смысла, хоткеев нет на touch).
 * - z-index 1000 — выше большинства AntD modals (1010 у них), но не перекрывает.
 * - Click + hover делают одну и ту же вещь (открыть modal).
 * - aria-label для a11y.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { HotkeyHelpModal } from './HotkeyHelpModal';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { MOBILE_MEDIA_QUERY } from '../constants/breakpoints';

export interface HotkeyHelpFloatingButtonProps {
  /** Test id для assertion в тестах. */
  testId?: string;
}

export function HotkeyHelpFloatingButton({
  testId = 'hotkey-help-floating-button',
}: HotkeyHelpFloatingButtonProps): React.ReactElement | null {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);

  if (isMobile) {
    return null;
  }

  return (
    <>
      <Tooltip title={t('hotkeys.floatingTooltip')} placement="left">
        <Button
          data-testid={testId}
          shape="circle"
          icon={<QuestionCircleOutlined />}
          onClick={() => setOpen(true)}
          aria-label={t('hotkeys.floatingAria')}
          style={{
            position: 'fixed',
            right: 20,
            bottom: 20,
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
          }}
        />
      </Tooltip>
      <HotkeyHelpModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export default HotkeyHelpFloatingButton;
